import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { FullScreenProgress } from '@occc-app/design-system';
import { RetailerNetworkLocationFilter, Retailer_Network_Performance_Period } from '@occc/graphql';

import { RetailerNetworkLocationDetailsPage } from './retailer-network-location-details-page.component';

import {
  useGetRetailerNetworkLocations,
  useGetRetailerNetworkLocationsWithPerformanceExcerpt,
  useGetRetailerNetworkQuery,
} from '../../data-access/queries';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { useSetLocationSpecificGoals } from '../components/retailer-network-performance/hooks/use-set-location-specific-goals.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';

export const RetailerNetworkLocationDetailsPageContainer = () => {
  const navigate = useNavigate();
  const { networkId = '', locationId = '' } = useParams<{
    networkId: string;
    locationId: string;
  }>();

  const setLocationPerformanceGoals = useSetLocationSpecificGoals({
    locationUuid: locationId,
    retailerNetworkUuid: networkId,
  });

  const { data: retailerNetworkLocationWithPerformanceExcerpt } =
    useGetRetailerNetworkLocationsWithPerformanceExcerpt({
      filter: {
        retailerNetworkUuidEquals: networkId,
        locationUuidEquals: locationId,
        // Period is not important here, as we are only interested in the isSiteSpecificGoal field
        // After the API for this query will be updated, we can remove this filter
        periodEquals: Retailer_Network_Performance_Period.ThisMonth,
      },
    });
  const isSpecificGoalSet = !!retailerNetworkLocationWithPerformanceExcerpt?.[0]?.isSpecificGoal;

  const {
    data: retailerNetworkData,
    loading: isRetailerNetworkDataLoading,
    error: retailerNetworkDataError,
  } = useGetRetailerNetworkQuery({
    variables: { uuid: networkId },
  });

  const locationFilter: RetailerNetworkLocationFilter = {
    retailerNetworkUuidEquals: networkId,
    retailerNetworkLocationUuidEquals: locationId,
  };
  const {
    data: retailerNetworkLocationData,
    loading: isRetailerNetworkLocationDataLoading,
    error: retailerNetworkLocationDataError,
  } = useGetRetailerNetworkLocations({
    filter: locationFilter,
  });

  const location = retailerNetworkLocationData?.[0]?.location;
  const locationName = location?.name || location?.siteSystemConfiguration?.[0]?.identifier;

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId) },
      { label: locationName || 'Store Details' },
    ],
    [locationName, networkId],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  const data = retailerNetworkData && location;
  const loading = isRetailerNetworkDataLoading || isRetailerNetworkLocationDataLoading;
  const error = retailerNetworkDataError || retailerNetworkLocationDataError;

  if (loading && !data) {
    return <FullScreenProgress />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!data) {
    return <RetailerNetworkNoData />;
  }

  const navigateToEditLocation = () => {
    navigate(routeCreator.locationUpdate(networkId, locationId));
  };

  return (
    <RetailerNetworkLocationDetailsPage
      location={location}
      retailerNetwork={retailerNetworkData.retailerNetwork}
      isSpecificGoalSet={isSpecificGoalSet}
      navigateToEditLocation={navigateToEditLocation}
      setLocationPerformanceGoals={setLocationPerformanceGoals}
    />
  );
};
